import { Trans, useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { useUpdateArticle } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { Currency, formatCurrencyRange } from '@/utils/number';

export const EstimatedValue = () => {
  const { t } = useLingui();
  const { request, workflow } = useRequestContext();
  const { article, view, errors } = useArticleContext();

  const { mutate: updateArticle } = useUpdateArticle(
    {
      articleId: article.id,
      requestId: request.id,
    },
    {
      optimistic: true,
    }
  );

  const estimatedValuesOptionsPerCurrency = workflow?.config.estimatedValues ?? {};
  const availableCurrencies = Object.keys(estimatedValuesOptionsPerCurrency) as Currency[];

  if (
    !view.details.estimatedValue.shown ||
    !estimatedValuesOptionsPerCurrency ||
    !availableCurrencies.length
  ) {
    return null;
  }

  const defaultCurrencyForRequest =
    request.afterSales?.afterSalesConfig?.defaultOrganizationPriceCurrency ?? 'EUR';
  const currency = availableCurrencies.includes(defaultCurrencyForRequest)
    ? defaultCurrencyForRequest
    : availableCurrencies[0];

  const estimatedValuesOptions = estimatedValuesOptionsPerCurrency[currency] ?? [];

  if (!estimatedValuesOptions.length) {
    return null;
  }

  const options = estimatedValuesOptions.map((value) => ({
    id: value === 'unknown' ? 'unknown' : `${value.min}-${value.max}`,
    value,
    label:
      value === 'unknown' ? (
        <Trans id="article.form.estimatedValue.unknown">Unknown</Trans>
      ) : (
        formatCurrencyRange(
          {
            min: value.min,
            max: value.max,
            currency,
          },
          {
            maximumFractionDigits: 0,
          }
        )
      ),
  }));

  const currentOption = article.data.estimatedValue
    ? article.data.estimatedValue === 'unknown'
      ? 'unknown'
      : `${article.data.estimatedValue.min}-${article.data.estimatedValue.max}`
    : null;

  return (
    <>
      {view.details.estimatedValue.shown && (
        <InputSelect
          variant="select"
          label={
            <>
              <Trans id="article.form.estimatedValue.label">Estimated Value</Trans>
              {!view.details.estimatedValue.required && <OptionalLabel />}
            </>
          }
          placeholder={t({
            id: 'article.form.estimatedValue.placeholder',
            message: 'Select the estimated value...',
          })}
          options={options}
          value={options.find((option) => option.id === currentOption) ?? null}
          onChange={(option) => {
            updateArticle({
              data: {
                data: {
                  ...article.data, // Preserve other data
                  estimatedValue: option
                    ? option.value === 'unknown'
                      ? 'unknown'
                      : {
                          ...option.value,
                          currency,
                        }
                    : undefined,
                },
              },
            });
          }}
          error={
            errors?.details?.missingEstimatedValue
              ? t({
                  id: 'article.form.estimated-value.error',
                  message: 'Please select an estimated value',
                })
              : undefined
          }
        />
      )}
    </>
  );
};
